"use client";

import { SlidersIcon } from "@phosphor-icons/react/dist/ssr";
import { useTranslations } from "next-intl";

import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  rightSideDrawerContentClassName,
} from "@/components/ui/drawer";
import { DrawerFooterActions } from "@/components/ui/drawerFooterActions";
import { FilterButton } from "@/components/ui/filterButton";
import { IndicatorDot } from "@/components/ui/indicatorDot";
import { DEADLINE_FILTER_OPTIONS, PROJECT_STATUS_FILTER_OPTIONS } from "@/constants";
import { useUser } from "@/hooks/usePermissions";

import { useProjectFilterDrawer } from "../hooks/useProjectFilterDrawer";
import { type FilterAssetOption, FilterScopeFields } from "./filterScopeFields";

export function ProjectFilterDrawer() {
  const t = useTranslations("home");
  const tGlossary = useTranslations("glossary");
  const user = useUser();
  const {
    open,
    draft,
    hasAppliedFilters,
    hasDraftFilters,
    reset,
    handleOpenChange,
    handleApply,
    handleStandardChange,
    handleToggleDeadline,
    handleToggleStatus,
    assetOptions,
    assignedProjectsData,
    handleAssetsChange,
    handleProjectChange,
    handleSiteChange,
    isProjectSelected,
    siteOptions,
    standardsData,
  } = useProjectFilterDrawer({ role: user.role });

  return (
    <Drawer direction="right" open={open} onOpenChange={handleOpenChange}>
      <DrawerTrigger asChild>
        <div className="relative inline-flex">
          <Button
            variant="outline-neutral"
            size="icon-md"
            aria-label={t("filter.aria")}
            id="filter-project-button"
            className="hover:bg-(--button-ghost-background-hover)"
          >
            <SlidersIcon />
          </Button>
          {hasAppliedFilters && <IndicatorDot className="absolute -top-1 -right-1 size-3.5" />}
        </div>
      </DrawerTrigger>

      <DrawerContent className={rightSideDrawerContentClassName} aria-describedby={undefined}>
        <DrawerHeader>
          <DrawerTitle>{t("filter.title")}</DrawerTitle>
        </DrawerHeader>

        <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-6">
          <FilterScopeFields
            standards={standardsData}
            assignedProjects={assignedProjectsData}
            siteOptions={siteOptions}
            assetOptions={assetOptions as FilterAssetOption[]}
            selectedStandard={draft.selectedStandard}
            selectedProject={draft.selectedAssignedProject}
            selectedSite={draft.selectedSite}
            selectedAssets={draft.selectedAssets}
            isProjectSelected={isProjectSelected}
            onStandardChange={handleStandardChange}
            onProjectChange={handleProjectChange}
            onSiteChange={handleSiteChange}
            onAssetsChange={handleAssetsChange}
          />

          <div className="flex flex-col gap-2 border-b border-neutral-100 pb-6">
            <h3 className="font-heading text-[14px] leading-none font-semibold text-neutral-950">
              {t("filter.deadline")}
            </h3>
            <div className="flex flex-wrap gap-2">
              {DEADLINE_FILTER_OPTIONS.map((option) => (
                <FilterButton
                  key={option}
                  selected={draft.deadlineFilters.includes(option)}
                  onClick={() => handleToggleDeadline(option)}
                >
                  {tGlossary(`deadlineStatus.${option}`)}
                </FilterButton>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2 border-b border-neutral-100 pb-6">
            <h3 className="font-heading text-[14px] leading-none font-semibold text-neutral-950">
              {t("filter.status")}
            </h3>
            <div className="flex flex-wrap gap-2">
              {PROJECT_STATUS_FILTER_OPTIONS.map((option) => (
                <FilterButton
                  key={option}
                  selected={draft.projectStatusFilters.includes(option)}
                  onClick={() => handleToggleStatus(option)}
                >
                  {tGlossary(`projectStatus.${option}`)}
                </FilterButton>
              ))}
            </div>
          </div>
        </div>

        <DrawerFooter className="border-t border-neutral-100 p-6">
          <DrawerFooterActions
            fullWidth
            secondaryAction={{ label: t("filter.reset"), onClick: reset }}
            primaryAction={{
              label: t("filter.apply"),
              onClick: handleApply,
              disabled: !hasDraftFilters,
            }}
          />
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
